// The main menu, as data. Grouped by what the user is doing — define the
// model, ask it questions, read the results, run the place — and the group
// boundaries are what the header draws as dividers.

export type NavGroup = 'model' | 'ask' | 'read' | 'run';

export interface NavEntry {
  to: string;
  label: string;
  group: NavGroup;
}

interface NavDefinition extends NavEntry {
  /** Page-registry key the admin can hide or scope; null for always-on. */
  key: string | null;
  requires?: 'import' | 'console' | 'visualize' | 'taxonomy';
}

const NAV: readonly NavDefinition[] = [
  { key: 'ontology', to: '/ontology', label: 'Ontology', group: 'model' },
  { key: 'vocabulary', to: '/vocabulary', label: 'Vocabulary', group: 'model', requires: 'taxonomy' },
  { key: 'entity', to: '/entity', label: 'Entities', group: 'model' },
  { key: 'query', to: '/query', label: 'Query', group: 'ask' },
  { key: 'viz', to: '/viz', label: 'Viz', group: 'ask', requires: 'visualize' },
  { key: 'trainer', to: '/trainer', label: 'LLM Testbed', group: 'ask' },
  { key: 'dashboard', to: '/dashboard', label: 'Dashboard', group: 'read' },
  { key: 'catalog', to: '/catalog', label: 'Catalog', group: 'read' },
  { key: 'timeline', to: '/timeline', label: 'Timeline', group: 'read' },
  { key: 'admin-load', to: '/admin/database', label: 'Data Management', group: 'run', requires: 'import' },
  { key: null, to: '/admin', label: 'Admin', group: 'run', requires: 'console' },
];

export function visibleNavEntries(
  pageVisible: (key: string) => boolean,
  rights: { canImport: boolean; canConsole: boolean; canVisualize: boolean; canBrowseTaxonomy: boolean },
): NavEntry[] {
  return NAV.filter((e) => {
    if (e.requires === 'import' && !rights.canImport) return false;
    if (e.requires === 'console' && !rights.canConsole) return false;
    if (e.requires === 'visualize' && !rights.canVisualize) return false;
    if (e.requires === 'taxonomy' && !rights.canBrowseTaxonomy) return false;
    return e.key === null || pageVisible(e.key);
  }).map(({ to, label, group }) => ({ to, label, group }));
}

export type NavRow = NavEntry | { divider: true };

/** Dividers land only between two entries of different groups. */
export function withDividers(entries: NavEntry[]): NavRow[] {
  const rows: NavRow[] = [];
  entries.forEach((entry, i) => {
    const prev = entries[i - 1];
    if (prev && prev.group !== entry.group) rows.push({ divider: true });
    rows.push(entry);
  });
  return rows;
}

/** The entry whose route the current path is on, for the collapsed menu label. */
export function currentNavEntry(entries: NavEntry[], pathname: string): NavEntry | undefined {
  return entries
    .filter((e) => pathname === e.to || pathname.startsWith(`${e.to}/`))
    .sort((a, b) => b.to.length - a.to.length)[0];
}
